import { useState } from 'react';

import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import { SplitButton } from 'primereact/splitbutton';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  useDeactivateDonorSchedulesMutation,
  useMassSetFieldMutation,
} from '@/app/services/donorSchedules';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import { PartnershipsActions } from '@/app/slices/partnerships';
import { addToast } from '@/app/slices/toast';
import { CommentDialog } from '@/components';
import { getRevisionCommentDialogConfig } from '@/components/common/revisions-button/revision-comment-dialog-config';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useReactivateDonorSchedules } from '@/services/donor-scheduling';
import { DonorSchedule } from '@/types.gql';

import { CLEARABLE_FIELDS } from '../../constants';
import { ClearableField } from '../../types';

type ActionsSplitButtonProps = {
  showAddRevisionCommentsDialog: boolean;
  setShowAddRevisionCommentsDialog: (show: boolean) => void;
  handleGetValue: (fieldName: string) => string | null;
  handleSetValue: (fieldName: string, value: string) => void;
  setMissingFieldsDialogVisible: (visible: boolean) => void;
};

export const ActionsSplitButton = ({
  showAddRevisionCommentsDialog,
  setShowAddRevisionCommentsDialog,
  handleGetValue,
  handleSetValue,
  setMissingFieldsDialogVisible,
}: ActionsSplitButtonProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [currentFields, setCurrentFields] = useState<ClearableField[] | null>(
    [],
  );
  const [clearDialogVisible, setClearDialogVisible] = useState(false);

  const { selectedDonorSchedules } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const [deactivateDonorSchedule] = useDeactivateDonorSchedulesMutation();
  const reactivateDonorSchedules = useReactivateDonorSchedules();
  const [massSetField] = useMassSetFieldMutation();

  const isMultiSelectEnabled = useFeatureFlag(
    'Select_Multiple_Chute_Schedules',
  );
  const revisionCommentDialogConfig =
    getRevisionCommentDialogConfig(isMultiSelectEnabled);

  const isReactivateSchedulesEnabled = useFeatureFlag(
    'DS_Reactivate_Schedules',
  );
  const isClearAspirationCommentsEnabled = useFeatureFlag(
    'DS_clear_aspiration_comments',
  );
  const isTransferFieldClearEnabled = useFeatureFlag(
    'DS_Transfer_Field_Clear',
  );
  const clearableFields = CLEARABLE_FIELDS.filter(
    (field) =>
      (isClearAspirationCommentsEnabled ||
        field.value !== 'specialInstructions') &&
      (isTransferFieldClearEnabled || field.value !== 'transfer'),
  );

  const selectedCount = selectedDonorSchedules?.length ?? 0;

  const handleDeactivate = async () => {
    try {
      const donorScheduleIds = selectedDonorSchedules?.map(
        (schedule: DonorSchedule) => schedule.id,
      );
      await deactivateDonorSchedule({ donorScheduleIds })
        .unwrap()
        .then(() => {
          dispatch({
            type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
          });
          dispatch({
            type: DonorSchedulingActions.RESET_SELECTED_DONOR_SCHEDULING,
          });
          dispatch({
            type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
          });
          dispatch({
            type: PartnershipsActions.RESET_PARTNERSHIP,
          });
        });
    } catch (error) {
      console.error('Failed to Deactivate donor schedules:', error);
    }
  };

  const handleDeactivateClick = () => {
    const scheduleNames = selectedDonorSchedules
      .map((schedule: DonorSchedule) => schedule.name)
      .join(', ');

    confirmDialog({
      message: isReactivateSchedulesEnabled ? (
        <div className="flex flex-col gap-3">
          <p className="m-0">
            {t('donorScheduling.deactivateConfirmCount', {
              count: selectedCount,
            })}
          </p>
          <ul className="m-0 max-h-60 list-disc overflow-y-auto pl-6">
            {selectedDonorSchedules?.map((schedule: DonorSchedule) => (
              <li key={schedule.id}>{schedule.name}</li>
            ))}
          </ul>
          <p className="m-0">{t('donorScheduling.confirmContinue')}</p>
        </div>
      ) : (
        t('donorScheduling.deactivateConfirmMessage', {
          names: scheduleNames,
        })
      ),
      header: t('donorScheduling.deactivateDonorSchedules'),
      icon: 'pi pi-exclamation-triangle',
      accept: handleDeactivate,
      acceptLabel: t('common.yes'),
      rejectLabel: t('common.no'),
    });
  };

  // Inactive schedules only: an all-active selection is a no-op, mirroring
  // how Deactivate behaves on already-inactive schedules.
  const inactiveSelectedSchedules = (selectedDonorSchedules ?? []).filter(
    (schedule: DonorSchedule) => schedule.stateCode === 1,
  );

  const handleReactivate = async () => {
    if (inactiveSelectedSchedules.length === 0) return;

    try {
      const donorScheduleIds = inactiveSelectedSchedules.map(
        (schedule: DonorSchedule) => schedule.id,
      );
      await reactivateDonorSchedules.mutateAsync(donorScheduleIds);

      dispatch({
        type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
      });
      dispatch({
        type: DonorSchedulingActions.RESET_SELECTED_DONOR_SCHEDULING,
      });
      dispatch({
        type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
      });
      dispatch({
        type: PartnershipsActions.RESET_PARTNERSHIP,
      });
    } catch (error) {
      console.error('Failed to Reactivate donor schedules:', error);
    }
  };

  const handleReactivateClick = () => {
    if (inactiveSelectedSchedules.length === 0) return;

    confirmDialog({
      message: (
        <div className="flex flex-col gap-3">
          <p className="m-0">
            {t('donorScheduling.reactivateConfirmCount', {
              count: inactiveSelectedSchedules.length,
            })}
          </p>
          <ul className="m-0 max-h-60 list-disc overflow-y-auto pl-6">
            {inactiveSelectedSchedules.map((schedule: DonorSchedule) => (
              <li key={schedule.id}>{schedule.name}</li>
            ))}
          </ul>
          <p className="m-0">{t('donorScheduling.confirmContinue')}</p>
        </div>
      ),
      header: t('donorScheduling.reactivateDonorSchedules'),
      icon: 'pi pi-exclamation-triangle',
      accept: handleReactivate,
      acceptLabel: t('common.yes'),
      rejectLabel: t('common.no'),
    });
  };

  const handleClear = async () => {
    if (currentFields?.length === 0) return;

    try {
      const donorScheduleIds = selectedDonorSchedules?.map(
        (schedule: DonorSchedule) => schedule.id,
      );

      let fieldsToClear = currentFields ? [...currentFields] : [];

      if (currentFields?.includes('allSemenRelatedFields')) {
        fieldsToClear = fieldsToClear.filter(
          (field) => field !== 'allSemenRelatedFields',
        );
        fieldsToClear.push(
          'semenId',
          'cane',
          'colCode',
          'naab',
          'sireId',
          'semenType',
          'semenLocationId',
          'semenOwnerId',
          'semenComment',
        );
      }

      await massSetField({
        donorScheduleIds,
        fields: fieldsToClear,
        value: '',
      })
        .unwrap()
        .then(() => {
          dispatch({
            type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
          });
          dispatch({
            type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
          });
          dispatch({
            type: PartnershipsActions.RESET_PARTNERSHIP,
          });
          dispatch(
            addToast({
              severity: 'success',
              summary: t('clearField.fieldsSuccessfullyCleared'),
              detail: t('clearField.fieldsSuccessfullyClearedDetail', {
                count: selectedCount,
              }),
              life: ToastLife.DEFAULT,
            }),
          );
        });
      setClearDialogVisible(false);
    } catch (error) {
      console.error('Failed to mass clear field:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('clearField.failedToClearFields'),
          detail: `${error}`,
          life: ToastLife.DEFAULT,
        }),
      );
    }
  };

  const clearDialogFooter = (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={() => setClearDialogVisible(false)}
        className="flex items-center gap-2 rounded-md border-none bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-300"
      >
        <i className="pi pi-times" />
        {t('common.cancel')}
      </button>
      <button
        type="button"
        onClick={handleClear}
        disabled={currentFields?.length === 0}
        className="flex items-center gap-2 rounded-md border-none bg-blue-400 px-4 py-2 text-white hover:bg-blue-500 focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <i className="pi pi-check" />
        {t('common.clear')}
      </button>
    </div>
  );

  const actionItems = [
    {
      label: t('common.deactivate'),
      icon: 'pi pi-trash',
      command: handleDeactivateClick,
    },
    ...(isReactivateSchedulesEnabled
      ? [
          {
            label: t('common.reactivate'),
            icon: 'pi pi-replay',
            command: handleReactivateClick,
          },
        ]
      : []),
    {
      label: t('clearField.clearField'),
      icon: 'pi pi-eraser',
      command: () => setClearDialogVisible(true),
    },
    {
      label: t('common.missingFields'),
      icon: 'pi pi-question',
      command: () => setMissingFieldsDialogVisible(true),
    },
    {
      label: t('revision.revisionComments'),
      icon: 'pi pi-plus',
      command: () => {
        if (
          selectedCount >= 1 &&
          (selectedCount === 1 || isMultiSelectEnabled)
        ) {
          setShowAddRevisionCommentsDialog(true);
        } else {
          dispatch(
            addToast({
              severity: 'error',
              summary: t('revision.revisionComments'),
              detail: t('donorScheduling.selectOneDonorSchedule'),
              life: ToastLife.DEFAULT,
            }),
          );
        }
      },
    },
  ];

  return (
    <>
      <SplitButton
        label={t('common.actions')}
        icon="pi pi-cog"
        model={actionItems}
        severity="secondary"
        disabled={selectedCount === 0}
      />

      <Dialog
        visible={clearDialogVisible}
        onHide={() => setClearDialogVisible(false)}
        header={t('clearField.clearField')}
        footer={clearDialogFooter}
        className="min-w-[450px]"
      >
        <div className="flex flex-col gap-4">
          <p className="m-0 leading-relaxed">
            {t('clearField.selectFieldsToClearForSchedules')}
          </p>
          <ul className="list-disc pl-6">
            {selectedDonorSchedules?.map((schedule: DonorSchedule) => (
              <li key={schedule.id}>{schedule.name}</li>
            ))}
          </ul>

          <div className="flex w-full gap-2">
            <MultiSelect
              value={currentFields}
              options={clearableFields}
              optionLabel="label"
              optionValue="value"
              display="chip"
              maxSelectedLabels={3}
              onChange={(e) => setCurrentFields(e.value as ClearableField[])}
              placeholder={t('clearField.selectFieldToClear')}
              filter={true}
              showSelectAll={false}
              className="flex-1"
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={showAddRevisionCommentsDialog}
        onHide={() => setShowAddRevisionCommentsDialog(false)}
        className="w-[80vw]"
      >
        <CommentDialog
          config={revisionCommentDialogConfig}
          setValue={handleSetValue}
          getValue={handleGetValue}
          onClose={() => setShowAddRevisionCommentsDialog(false)}
        />
      </Dialog>
    </>
  );
};
